import React, { useState, useEffect, useRef } from 'react';
import { dbService } from '../firebaseConfig';
import { Registration, EventSettings, ScheduleDay, ScheduleItem, Speaker } from '../types';
import Logo from '../components/Logo';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  type: 'danger' | 'success';
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ isOpen, onClose, onConfirm, title, message, type }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-brand-night/50 backdrop-blur-sm animate-in fade-in" onClick={onClose}></div>
      <div className="relative bg-white rounded-[40px] shadow-2xl p-8 max-w-sm w-full border border-brand-sand animate-in zoom-in slide-in-from-bottom-4 duration-300">
        <div className="text-center">
          <div className={`w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-6 text-3xl shadow-lg ${type === 'danger' ? 'bg-red-50 text-red-500 shadow-red-100' : 'bg-green-50 text-brand-green shadow-green-100'}`}>
            {type === 'danger' ? '🗑️' : '✅'}
          </div>
          <h3 className="font-display text-2xl font-semibold uppercase text-brand-dark mb-3">{title}</h3>
          <p className="text-brand-stone text-sm leading-relaxed mb-8">{message}</p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => { onConfirm(); onClose(); }}
              className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95 shadow-lg ${type === 'danger' ? 'bg-red-500 text-white shadow-red-200' : 'bg-brand-green text-white shadow-green-200'}`}
            >
              Sim, Confirmar
            </button>
            <button
              onClick={onClose}
              className="w-full py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] text-brand-stone hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Helpers de programação (texto <-> estrutura) ---------- */
const scheduleToText = (items: ScheduleItem[]) =>
  items.map(i => [i.time, i.activity, i.who || '', i.icon || ''].join(' | ').replace(/( \| )+$/, '')).join('\n');

const textToSchedule = (text: string): ScheduleItem[] =>
  text.split('\n')
    .map(l => l.trim())
    .filter(Boolean)
    .map(line => {
      const [time = '', activity = '', who = '', icon = ''] = line.split('|').map(p => p.trim());
      const item: ScheduleItem = { time, activity };
      if (who) item.who = who;
      if (icon) item.icon = icon;
      return item;
    });

const inputClass = "w-full p-4 bg-brand-sand/60 rounded-2xl border-none outline-none focus:ring-2 focus:ring-brand-amber transition-all text-sm";
const labelClass = "text-[10px] font-black uppercase text-brand-stone tracking-widest ml-1";

const Card: React.FC<{ icon: string; title: string; subtitle: string; children: React.ReactNode }> = ({ icon, title, subtitle, children }) => (
  <div className="bg-white p-8 md:p-10 rounded-[40px] border border-brand-sand shadow-xl space-y-8">
    <div className="flex items-center gap-4">
      <span className="text-3xl">{icon}</span>
      <div>
        <h3 className="font-display text-2xl font-semibold uppercase text-brand-dark leading-none">{title}</h3>
        <p className="text-xs text-brand-stone mt-1">{subtitle}</p>
      </div>
    </div>
    {children}
  </div>
);

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'registrations' | 'settings'>('registrations');
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [settings, setSettings] = useState<EventSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [regFilter, setRegFilter] = useState<'all' | 'review' | 'pending' | 'paid'>('all');

  // Edição de preletores (arquivos pendentes de upload)
  const [speakerFiles, setSpeakerFiles] = useState<Record<string, File>>({});
  const [speakerPreviews, setSpeakerPreviews] = useState<Record<string, string>>({});
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  // Edição da programação e checklist como texto
  const [scheduleText, setScheduleText] = useState<Record<string, string>>({});
  const [checklistText, setChecklistText] = useState('');
  const [retreatNotesText, setRetreatNotesText] = useState('');

  // Modal State
  const [modal, setModal] = useState<{
    isOpen: boolean;
    type: 'danger' | 'success';
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [regs, sett] = await Promise.all([
        dbService.getRegistrations(),
        dbService.getSettings()
      ]);
      setRegistrations(regs);
      setSettings(sett);

      const st: Record<string, string> = {};
      sett.schedule.forEach(d => { st[d.id] = scheduleToText(d.items); });
      setScheduleText(st);
      setChecklistText(sett.checklist.join('\n'));
      setRetreatNotesText((sett.retreatNotes || []).join('\n'));

      const previews: Record<string, string> = {};
      sett.speakers.forEach(s => { previews[s.id] = s.image; });
      setSpeakerPreviews(previews);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;
    setSaving(true);
    try {
      // Upload de fotos novas dos preletores
      const speakers: Speaker[] = [];
      for (const sp of settings.speakers) {
        let image = sp.image;
        if (speakerFiles[sp.id]) image = await dbService.uploadImage(speakerFiles[sp.id]);
        speakers.push({ ...sp, image });
      }

      // Programação a partir do texto
      const schedule: ScheduleDay[] = settings.schedule.map(d => ({
        ...d,
        items: textToSchedule(scheduleText[d.id] ?? scheduleToText(d.items))
      }));

      const checklist = checklistText.split('\n').map(l => l.trim()).filter(Boolean);
      const retreatNotes = retreatNotesText.split('\n').map(l => l.trim()).filter(Boolean);

      const updatedSettings: EventSettings = { ...settings, speakers, schedule, checklist, retreatNotes };
      await dbService.saveSettings(updatedSettings);
      setSettings(updatedSettings);
      setSpeakerFiles({});
      alert('Configurações salvas com sucesso!');
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar configurações.');
    } finally {
      setSaving(false);
    }
  };

  const updateSpeaker = (id: string, patch: Partial<Speaker>) => {
    if (!settings) return;
    setSettings({ ...settings, speakers: settings.speakers.map(s => s.id === id ? { ...s, ...patch } : s) });
  };

  const addSpeaker = () => {
    if (!settings) return;
    const id = `preletor-${Date.now()}`;
    setSettings({ ...settings, speakers: [...settings.speakers, { id, name: '', title: '', image: '', sessions: '' }] });
  };

  const removeSpeaker = (id: string) => {
    if (!settings) return;
    setSettings({ ...settings, speakers: settings.speakers.filter(s => s.id !== id) });
  };

  const updateDay = (id: string, patch: Partial<ScheduleDay>) => {
    if (!settings) return;
    setSettings({ ...settings, schedule: settings.schedule.map(d => d.id === id ? { ...d, ...patch } : d) });
  };

  const openBaixaModal = (reg: Registration) => {
    setModal({
      isOpen: true,
      type: 'success',
      title: 'Dar Baixa Manual',
      message: `Deseja confirmar o pagamento de ${reg.fullName}? Esta ação não pode ser desfeita.`,
      onConfirm: () => dbService.updatePaymentStatus(reg.id!, 'paid').then(loadData)
    });
  };

  const openDeleteModal = (reg: Registration) => {
    setModal({
      isOpen: true,
      type: 'danger',
      title: 'Excluir Inscrição',
      message: `Tem certeza que deseja remover ${reg.fullName}? Todos os dados deste inscrito serão perdidos.`,
      onConfirm: () => dbService.deleteRegistration(reg.id!).then(loadData)
    });
  };

  const filtered = registrations
    .filter(r => r.fullName.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(r => {
      if (regFilter === 'review') return r.status !== 'paid' && (!!r.receiptUrl || !!r.receiptViaWhatsApp);
      if (regFilter === 'paid') return r.status === 'paid';
      if (regFilter === 'pending') return r.status !== 'paid' && !r.receiptUrl && !r.receiptViaWhatsApp;
      return true;
    });
  const paidCount = registrations.filter(r => r.status === 'paid').length;
  const reviewCount = registrations.filter(r => r.status !== 'paid' && (!!r.receiptUrl || !!r.receiptViaWhatsApp)).length;

  const formatDate = (ts?: number) => ts ? new Date(ts).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-sand-soft">
      <div className="w-12 h-12 border-4 border-brand-sand border-t-brand-amber rounded-full animate-spin mb-4"></div>
      <p className="text-brand-dark font-bold text-sm">Carregando painel...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-sand-soft py-10 px-4">
      <ConfirmModal
        isOpen={modal.isOpen}
        onClose={() => setModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />

      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col md:flex-row justify-between items-center mb-10 gap-6">
          <div className="flex items-center gap-4">
            <Logo className="w-12 h-12 rounded-2xl" />
            <div>
              <h1 className="font-display text-3xl font-semibold uppercase text-brand-dark leading-none">Painel de Controle</h1>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-ember mt-1">Congresso de Homens • IPCC 2026</p>
            </div>
          </div>
          <div className="flex bg-white p-1 rounded-2xl border border-brand-sand shadow-sm">
            <button onClick={() => setActiveTab('registrations')} className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'registrations' ? 'bg-brand-dark text-white shadow-lg' : 'text-brand-stone hover:text-brand-dark'}`}>Inscrições</button>
            <button onClick={() => setActiveTab('settings')} className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'settings' ? 'bg-brand-dark text-white shadow-lg' : 'text-brand-stone hover:text-brand-dark'}`}>Ajustes Site</button>
          </div>
        </header>

        {/* ================= INSCRIÇÕES ================= */}
        {activeTab === 'registrations' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder="Buscar inscrito por nome..."
                  className="w-full p-4 pl-12 rounded-[24px] border-none shadow-sm bg-white outline-none focus:ring-2 focus:ring-brand-amber transition-all"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-300">🔍</span>
              </div>
              <div className="bg-white px-8 py-4 rounded-[24px] shadow-sm border border-brand-sand flex items-center justify-center gap-4">
                <span className="text-brand-stone text-[10px] font-black uppercase tracking-widest">Inscrições</span>
                <span className="font-display text-3xl font-semibold text-brand-dark leading-none">{filtered.length}</span>
              </div>
              <div className="bg-white px-8 py-4 rounded-[24px] shadow-sm border border-brand-sand flex items-center justify-center gap-4">
                <span className="text-brand-stone text-[10px] font-black uppercase tracking-widest">Pagos</span>
                <span className="font-display text-3xl font-semibold text-brand-green leading-none">{paidCount}</span>
              </div>
              <button
                type="button"
                onClick={() => setRegFilter(regFilter === 'review' ? 'all' : 'review')}
                className={`px-8 py-4 rounded-[24px] shadow-sm border flex items-center justify-center gap-4 transition-all ${regFilter === 'review' ? 'bg-brand-amber border-brand-amber text-brand-night' : 'bg-white border-brand-sand'}`}
              >
                <span className={`text-[10px] font-black uppercase tracking-widest ${regFilter === 'review' ? 'text-brand-night/70' : 'text-brand-stone'}`}>A conferir</span>
                <span className={`font-display text-3xl font-semibold leading-none ${regFilter === 'review' ? 'text-brand-night' : 'text-brand-ember'}`}>{reviewCount}</span>
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {([['all', 'Todos'], ['review', 'A conferir (anexo ou WhatsApp)'], ['pending', 'Sem comprovante'], ['paid', 'Pagos']] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setRegFilter(key)}
                  className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${regFilter === key ? 'bg-brand-dark text-white' : 'bg-white border border-brand-sand text-brand-stone hover:text-brand-dark'}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="bg-white rounded-[40px] shadow-xl overflow-hidden border border-brand-sand">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-brand-sand/40 text-[10px] uppercase font-black tracking-[0.2em] text-brand-stone">
                    <tr>
                      <th className="px-8 py-5">Inscrito</th>
                      <th className="px-8 py-5">Igreja</th>
                      <th className="px-8 py-5">Status</th>
                      <th className="px-8 py-5">Pagamento</th>
                      <th className="px-8 py-5">Comprovante</th>
                      <th className="px-8 py-5 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-sand/60">
                    {filtered.length === 0 && (
                      <tr><td colSpan={6} className="px-8 py-12 text-center text-brand-stone text-sm italic">Nenhuma inscrição encontrada.</td></tr>
                    )}
                    {filtered.map(reg => (
                      <tr key={reg.id} className="hover:bg-brand-sand/30 transition-colors group">
                        <td className="px-8 py-6">
                          <p className="font-bold text-gray-800 text-lg">{reg.fullName}</p>
                          <p className="text-xs text-brand-stone font-medium">{reg.email} • {reg.phone}</p>
                        </td>
                        <td className="px-8 py-6 text-sm text-brand-stone">{reg.church || '—'}</td>
                        <td className="px-8 py-6">
                          <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap ${reg.status === 'paid' ? 'bg-green-100 text-green-700' : reg.receiptUrl ? 'bg-orange-100 text-orange-700' : reg.receiptViaWhatsApp ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>
                            {reg.status === 'paid' ? 'Pago' : reg.receiptUrl ? 'A conferir' : reg.receiptViaWhatsApp ? 'Via WhatsApp' : 'Pendente'}
                          </span>
                        </td>
                        <td className="px-8 py-6 text-sm text-gray-500">
                          <div className="flex flex-col">
                            <span className="font-bold text-brand-dark">{reg.paymentMethod === 'pix' ? '🌀 PIX' : '💳 CARTÃO'}</span>
                            <span className="text-[10px] text-brand-stone uppercase tracking-widest">R$ {reg.amount.toFixed(2)}</span>
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          {reg.receiptUrl ? (
                            <a
                              href={reg.receiptUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-3 group/receipt"
                              title={reg.receiptName}
                            >
                              <div className="w-14 h-14 rounded-xl bg-brand-sand border border-brand-sand overflow-hidden flex items-center justify-center shrink-0 shadow-sm group-hover/receipt:scale-105 transition-transform">
                                {reg.receiptType?.startsWith('image/') ? (
                                  <img src={reg.receiptUrl} alt="Comprovante" className="w-full h-full object-cover" loading="lazy" />
                                ) : (
                                  <span className="text-2xl">📄</span>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-widest text-brand-green group-hover/receipt:underline">Ver comprovante</p>
                                <p className="text-[10px] text-brand-stone">{formatDate(reg.receiptUploadedAt)}</p>
                              </div>
                            </a>
                          ) : reg.receiptViaWhatsApp ? (
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-widest text-sky-700">📱 Vai enviar pelo WhatsApp</p>
                              <p className="text-[10px] text-brand-stone">Confira a conversa com {reg.phone}</p>
                            </div>
                          ) : (
                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-8 py-6 text-right space-x-4 whitespace-nowrap">
                          {reg.status !== 'paid' && (
                            <button
                              onClick={() => openBaixaModal(reg)}
                              className="text-[10px] font-black uppercase text-brand-green hover:underline tracking-widest"
                            >
                              Dar Baixa
                            </button>
                          )}
                          <button
                            onClick={() => openDeleteModal(reg)}
                            className="text-[10px] font-black uppercase text-red-400 hover:text-red-600 tracking-widest"
                          >
                            Excluir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ================= AJUSTES ================= */}
        {activeTab === 'settings' && settings && (
          <form onSubmit={handleSaveSettings} className="space-y-8 animate-in fade-in slide-in-from-bottom-4">

            <div className="bg-brand-dark text-white p-10 rounded-[40px] shadow-2xl relative overflow-hidden">
              <div className="absolute inset-0 texture-lines"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-amber/10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="text-center md:text-left">
                  <h3 className="font-display text-3xl font-semibold uppercase mb-2">Método de Recebimento</h3>
                  <p className="text-white/60 text-sm max-w-md">Escolha se os inscritos podem pagar com cartão via PagSeguro ou se prefere receber apenas via PIX direto.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings({...settings, pauseAutomaticPayments: !settings.pauseAutomaticPayments})}
                  className={`px-10 py-5 rounded-[24px] font-black uppercase tracking-widest text-sm transition-all shadow-xl active:scale-95 ${settings.pauseAutomaticPayments ? 'bg-brand-amber text-brand-night' : 'bg-white text-brand-dark'}`}
                >
                  {settings.pauseAutomaticPayments ? '✅ MODO SOMENTE PIX ATIVO' : '💳 PAGSEGURO ATIVADO'}
                </button>
              </div>
            </div>

            {settings.eventPrice <= 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-6 rounded-3xl text-sm flex items-center gap-4">
                <span className="text-2xl">⚠️</span>
                <p><b>O valor da inscrição está em R$ 0.</b> Defina o preço em "Detalhes do Evento" antes de abrir as inscrições — enquanto for zero o site não exibe valor no botão.</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* Coluna esquerda */}
              <div className="space-y-8">
                <Card icon="📝" title="Status das Inscrições" subtitle="Ative ou desative a possibilidade de novas inscrições.">
                  <button
                    type="button"
                    onClick={() => setSettings({...settings, isRegistrationClosed: !settings.isRegistrationClosed})}
                    className={`w-full px-10 py-5 rounded-[24px] font-black uppercase tracking-widest text-sm transition-all shadow-xl active:scale-95 ${settings.isRegistrationClosed ? 'bg-red-500 text-white shadow-red-100' : 'bg-green-500 text-white shadow-green-100'}`}
                  >
                    {settings.isRegistrationClosed ? '🔒 INSCRIÇÕES ENCERRADAS' : '🔓 INSCRIÇÕES ABERTAS'}
                  </button>
                </Card>

                <Card icon="📅" title="Detalhes do Evento" subtitle="Nome, datas, versículo e valor da inscrição.">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Nome do evento</label>
                      <input className={inputClass} value={settings.eventName} onChange={e => setSettings({...settings, eventName: e.target.value})} />
                    </div>
                    <div>
                      <label className={labelClass}>Edição / subtítulo</label>
                      <input className={inputClass} value={settings.eventEdition} onChange={e => setSettings({...settings, eventEdition: e.target.value})} />
                    </div>
                    <div>
                      <label className={labelClass}>Datas exibidas</label>
                      <input className={inputClass} placeholder="16 a 18 de Outubro" value={settings.eventDate} onChange={e => setSettings({...settings, eventDate: e.target.value})} />
                    </div>
                    <div>
                      <label className={labelClass}>Ano</label>
                      <input className={inputClass} value={settings.eventYear} onChange={e => setSettings({...settings, eventYear: e.target.value})} />
                    </div>
                    <div>
                      <label className={labelClass}>Horário de chegada</label>
                      <input className={inputClass} value={settings.eventTime} onChange={e => setSettings({...settings, eventTime: e.target.value})} />
                    </div>
                    <div>
                      <label className={labelClass}>Preço Inscrição (R$)</label>
                      <input type="number" min={0} className={inputClass} value={settings.eventPrice} onChange={e => setSettings({...settings, eventPrice: Number(e.target.value)})} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClass}>Versículo tema</label>
                      <input className={inputClass} value={settings.eventVerse} onChange={e => setSettings({...settings, eventVerse: e.target.value})} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClass}>Referência do versículo</label>
                      <input className={inputClass} value={settings.eventVerseRef} onChange={e => setSettings({...settings, eventVerseRef: e.target.value})} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClass}>Frase de pagamento (aparece no início do site, na inscrição e no PIX; vazia = some)</label>
                      <input className={inputClass} placeholder="Pague por PIX ou diretamente na igreja, até o dia 09/10..." value={settings.paymentInfo || ''} onChange={e => setSettings({...settings, paymentInfo: e.target.value})} />
                    </div>
                  </div>
                </Card>

                <Card icon="📍" title="Locais" subtitle="Igreja (chegada/encerramento) e chácara (hospedagem).">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className={labelClass}>Nome da Igreja</label>
                      <input className={inputClass} value={settings.locationName} onChange={e => setSettings({...settings, locationName: e.target.value})} />
                    </div>
                    <div>
                      <label className={labelClass}>Endereço da Igreja</label>
                      <input className={inputClass} value={settings.locationAddress} onChange={e => setSettings({...settings, locationAddress: e.target.value})} />
                    </div>
                    <div>
                      <label className={labelClass}>Nome da Chácara</label>
                      <input className={inputClass} value={settings.retreatName} onChange={e => setSettings({...settings, retreatName: e.target.value})} />
                    </div>
                    <div>
                      <label className={labelClass}>Endereço da Chácara (opcional)</label>
                      <input className={inputClass} placeholder="Se vazio, o site diz que será enviado aos inscritos" value={settings.retreatAddress} onChange={e => setSettings({...settings, retreatAddress: e.target.value})} />
                    </div>
                    <div>
                      <label className={labelClass}>Link "Como chegar" da Chácara (Google Maps)</label>
                      <input className={inputClass} placeholder="https://www.google.com/maps/..." value={settings.retreatMapsLink || ''} onChange={e => setSettings({...settings, retreatMapsLink: e.target.value.trim()})} />
                    </div>
                    <div>
                      <label className={labelClass}>Mapa da Chácara — cole o código de incorporação do Google Maps (o src é extraído sozinho)</label>
                      <textarea
                        rows={3}
                        className={`${inputClass} font-mono text-xs leading-relaxed`}
                        placeholder='<iframe src="https://www.google.com/maps/embed?pb=..."></iframe>'
                        value={settings.retreatMapEmbed || ''}
                        onChange={e => {
                          let v = e.target.value.trim();
                          const m = v.match(/src="([^"]+)"/);
                          if (m) v = m[1];
                          setSettings({...settings, retreatMapEmbed: v});
                        }}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Avisos do card da Chácara (um por linha; comece com um emoji)</label>
                      <textarea
                        rows={Math.max(4, retreatNotesText.split('\n').length + 1)}
                        className={`${inputClass} leading-relaxed`}
                        placeholder={'🚌 Saída em grupo da Igreja na sexta...'}
                        value={retreatNotesText}
                        onChange={e => setRetreatNotesText(e.target.value)}
                      />
                    </div>
                  </div>
                </Card>

                <Card icon="🌀" title="Dados para PIX e Contato" subtitle="Configure sua chave e o WhatsApp para comprovantes.">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className={labelClass}>Chave PIX (CPF/CNPJ/Email)</label>
                      <input placeholder="78.118.353/0001-88" className={inputClass} value={settings.pixKey} onChange={e => setSettings({...settings, pixKey: e.target.value})} />
                    </div>
                    <div>
                      <label className={labelClass}>Nome do Recebedor (sem acentos)</label>
                      <input placeholder="IPCC" className={inputClass} value={settings.pixReceiver} onChange={e => setSettings({...settings, pixReceiver: e.target.value})} />
                    </div>
                    <div>
                      <label className={labelClass}>WhatsApp para Comprovantes</label>
                      <input placeholder="45999999999 (apenas números)" className={inputClass} value={settings.contactWhatsApp || ''} onChange={e => setSettings({...settings, contactWhatsApp: e.target.value.replace(/\D/g, '')})} />
                    </div>
                  </div>

                  {!settings.pauseAutomaticPayments && (
                    <div className="space-y-4 pt-6 border-t border-brand-sand animate-in fade-in">
                      <h4 className="font-black text-[10px] uppercase tracking-[0.3em] text-brand-stone">Credenciais PagSeguro</h4>
                      <div className="grid grid-cols-1 gap-4">
                        <input placeholder="E-mail" className={inputClass} value={settings.pagSeguroEmail || ''} onChange={e => setSettings({...settings, pagSeguroEmail: e.target.value})} />
                        <input type="password" placeholder="Token de Integração" className={inputClass} value={settings.pagSeguroToken || ''} onChange={e => setSettings({...settings, pagSeguroToken: e.target.value})} />
                      </div>
                    </div>
                  )}
                </Card>
              </div>

              {/* Coluna direita */}
              <div className="space-y-8">
                <Card icon="🎤" title="Preletores" subtitle="O primeiro da lista aparece em destaque no centro da capa.">
                  <div className="space-y-6">
                    {settings.speakers.map((sp, idx) => (
                      <div key={sp.id} className="flex gap-5 items-start p-5 rounded-3xl bg-brand-sand/40 border border-brand-sand">
                        <div
                          onClick={() => fileInputs.current[sp.id]?.click()}
                          className="w-24 h-28 bg-brand-dark rounded-2xl overflow-hidden cursor-pointer border-4 border-white shadow-xl flex items-end justify-center shrink-0 hover:scale-105 transition-transform"
                          title="Clique para trocar a foto (PNG com fundo transparente fica melhor)"
                        >
                          {speakerPreviews[sp.id] ? (
                            <img src={speakerPreviews[sp.id]} className="w-full h-full object-contain object-bottom" />
                          ) : (
                            <span className="text-3xl m-auto">📸</span>
                          )}
                          <input type="file" ref={el => { fileInputs.current[sp.id] = el; }} className="hidden" accept="image/*" onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setSpeakerFiles(prev => ({ ...prev, [sp.id]: file }));
                              setSpeakerPreviews(prev => ({ ...prev, [sp.id]: URL.createObjectURL(file) }));
                            }
                          }} />
                        </div>
                        <div className="flex-grow space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-widest text-brand-ember">{idx === 0 ? '★ Destaque' : `Preletor ${idx + 1}`}</span>
                            <button type="button" onClick={() => removeSpeaker(sp.id)} className="text-[10px] font-black uppercase text-red-400 hover:text-red-600 tracking-widest">Remover</button>
                          </div>
                          <input placeholder="Nome" className={inputClass} value={sp.name} onChange={e => updateSpeaker(sp.id, { name: e.target.value })} />
                          <input placeholder="Título (ex: Pastor, UDF...)" className={inputClass} value={sp.title} onChange={e => updateSpeaker(sp.id, { title: e.target.value })} />
                          <input placeholder="Participação (ex: Sexta • Sábado)" className={inputClass} value={sp.sessions || ''} onChange={e => updateSpeaker(sp.id, { sessions: e.target.value })} />
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={addSpeaker} className="w-full py-4 rounded-2xl border-2 border-dashed border-brand-sand text-brand-stone text-xs font-black uppercase tracking-widest hover:border-brand-amber hover:text-brand-amber transition-colors">
                      + Adicionar preletor
                    </button>
                  </div>
                </Card>

                <Card icon="🗓️" title="Programação" subtitle="Uma atividade por linha: horário | atividade | responsável | emoji.">
                  <div className="space-y-6">
                    {settings.schedule.map(d => (
                      <div key={d.id} className="space-y-3 p-5 rounded-3xl bg-brand-sand/40 border border-brand-sand">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <input className={inputClass} placeholder="Dia" value={d.label} onChange={e => updateDay(d.id, { label: e.target.value })} />
                          <input className={inputClass} placeholder="Data" value={d.date} onChange={e => updateDay(d.id, { date: e.target.value })} />
                          <input className={inputClass} placeholder="Local" value={d.place} onChange={e => updateDay(d.id, { place: e.target.value })} />
                        </div>
                        <textarea
                          rows={Math.max(4, (scheduleText[d.id] || '').split('\n').length + 1)}
                          className={`${inputClass} font-mono text-xs leading-relaxed`}
                          value={scheduleText[d.id] ?? ''}
                          onChange={e => setScheduleText(prev => ({ ...prev, [d.id]: e.target.value }))}
                          placeholder={"18h45 | Chegada e check-in | na Igreja | 🚪"}
                        />
                      </div>
                    ))}
                  </div>
                </Card>

                <Card icon="🎒" title="O que levar" subtitle="Um item por linha.">
                  <textarea
                    rows={Math.max(6, checklistText.split('\n').length + 1)}
                    className={`${inputClass} leading-relaxed`}
                    value={checklistText}
                    onChange={e => setChecklistText(e.target.value)}
                  />
                </Card>
              </div>
            </div>

            <button
              disabled={saving}
              type="submit"
              className="w-full bg-brand-dark text-white py-8 rounded-[40px] font-display font-semibold uppercase tracking-widest text-2xl shadow-2xl hover:bg-brand-night transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-4"
            >
              {saving ? 'Salvando...' : '💾 Salvar Configurações'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
